import React, { useState } from 'react';
import { Input, Button, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { createMaster, updateMaster, deleteMaster, createAgeingBand, updateAgeingBand, deleteAgeingBand } from './api';
import { useMasters } from './MastersContext';

const CATEGORIES = [
  { key: 'products', label: 'Products', apiKey: 'product' },
  { key: 'tests', label: 'Tests', apiKey: 'test' },
  { key: 'stages', label: 'Stages', apiKey: 'stage' },
  { key: 'stabilityConditions', label: 'Stability Conditions', apiKey: 'stability_condition' },
  { key: 'markets', label: 'Markets', apiKey: 'market' },
  { key: 'durations', label: 'Durations', apiKey: 'duration' },
  { key: 'analysisTypes', label: 'Analysis Types', apiKey: 'analysis_type' },
];

const Masters = () => {
  const { masters, ageingBands, refreshMasters } = useMasters();
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [addValues, setAddValues] = useState({});
  const [editingBandId, setEditingBandId] = useState(null);
  const [editingBand, setEditingBand] = useState({});

  /* ---- Master CRUD ---- */
  const handleAdd = async (category, apiKey, items) => {
    const val = (addValues[category] || '').trim();
    if (!val) return;
    try {
      await createMaster({ category: apiKey, value: val, sortOrder: items.length + 1 });
      await refreshMasters();
      setAddValues((prev) => ({ ...prev, [category]: '' }));
      message.success('Value added');
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleUpdate = async (id) => {
    if (!editValue.trim()) return;
    try {
      await updateMaster(id, { value: editValue.trim() });
      await refreshMasters();
      setEditingId(null);
      setEditValue('');
      message.success('Value updated');
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteMaster(id);
      await refreshMasters();
      message.success('Value deleted');
    } catch (err) {
      message.error(err.message);
    }
  };

  /* ---- Ageing Band CRUD ---- */
  const handleAddBand = async () => {
    try {
      await createAgeingBand({ name: 'New Band', minDays: 0, maxDays: 1, colorHex: '#cccccc' });
      await refreshMasters();
      message.success('Band added');
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleUpdateBand = async (id) => {
    try {
      await updateAgeingBand(id, editingBand);
      await refreshMasters();
      setEditingBandId(null);
      setEditingBand({});
      message.success('Band updated');
    } catch (err) {
      message.error(err.message);
    }
  };

  const handleDeleteBand = async (id) => {
    try {
      await deleteAgeingBand(id);
      await refreshMasters();
      message.success('Band deleted');
    } catch (err) {
      if (err.message && err.message.includes('400')) {
        message.error('Cannot delete — minimum 2 ageing bands required');
      } else {
        message.error(err.message);
      }
    }
  };

  /* ---- Render a single master category card ---- */
  const renderCard = ({ key, label, apiKey }) => {
    const items = masters[key] || [];
    return (
      <div
        key={key}
        style={{
          background: '#fff', borderRadius: 5, border: '1px solid #d1d5db',
          padding: '9px 11px',
          boxShadow: '0 1px 2px rgba(0,0,0,.06),0 1px 3px rgba(0,0,0,.08)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#1a3353' }}>{label}</span>
          <span style={{
            fontSize: 9, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700,
            background: '#f3f4f6', border: '1px solid #d1d5db',
            borderRadius: 3, padding: '1px 5px', color: '#4b5563',
          }}>
            {items.length}
          </span>
        </div>

        {/* Scrollable list */}
        <div style={{ maxHeight: 190, overflowY: 'auto', flex: 1, marginBottom: 5 }}>
          {items.map((item) => (
            <div
              key={item.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '2px 0', borderBottom: '1px solid #f5f5f5',
              }}
            >
              {editingId === item.id ? (
                <>
                  <input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdate(item.id)}
                    style={{
                      flex: 1, fontSize: 10, border: '1px solid #2563eb', borderRadius: 3,
                      padding: '1px 4px', outline: 'none', fontFamily: "'DM Sans', sans-serif",
                    }}
                    autoFocus
                  />
                  <button onClick={() => handleUpdate(item.id)} style={{
                    fontSize: 8, padding: '1px 4px', background: '#f0fdf4', color: '#059669',
                    border: 'none', borderRadius: 2, cursor: 'pointer',
                  }}>✓</button>
                  <button onClick={() => { setEditingId(null); setEditValue(''); }} style={{
                    fontSize: 8, padding: '1px 4px', background: '#f3f4f6', color: '#4b5563',
                    border: 'none', borderRadius: 2, cursor: 'pointer',
                  }}>✕</button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontSize: 10, color: '#4b5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.value}
                  </span>
                  <button
                    onClick={() => { setEditingId(item.id); setEditValue(item.value); }}
                    style={{
                      fontSize: 8, padding: '1px 4px', background: '#f3f4f6', color: '#4b5563',
                      border: 'none', borderRadius: 2, cursor: 'pointer', opacity: 0.7,
                    }}
                  >✎</button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    style={{
                      fontSize: 8, padding: '1px 4px', background: '#fef2f2', color: '#dc2626',
                      border: 'none', borderRadius: 2, cursor: 'pointer', opacity: 0.7,
                    }}
                  >✕</button>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add row */}
        <div style={{ display: 'flex', gap: 4, marginTop: 5 }}>
          <input
            placeholder={`Add ${label.toLowerCase()}`}
            value={addValues[key] || ''}
            onChange={(e) => setAddValues((prev) => ({ ...prev, [key]: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd(key, apiKey, items)}
            style={{
              flex: 1, fontSize: 10, border: '1px solid #d1d5db', borderRadius: 3,
              padding: '3px 5px', outline: 'none', fontFamily: "'DM Sans', sans-serif",
            }}
            onFocus={e => e.target.style.borderColor = '#2563eb'}
            onBlur={e => e.target.style.borderColor = '#d1d5db'}
          />
          <button onClick={() => handleAdd(key, apiKey, items)} style={{
            fontSize: 9, fontWeight: 700, padding: '3px 8px',
            background: '#2563eb', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer',
          }}>
            + Add
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px 20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 9 }}>
        {CATEGORIES.map(renderCard)}
      </div>

      {/* Ageing Bands — full width */}
      <div style={{
        marginTop: 9, background: '#fff', borderRadius: 5, border: '1px solid #d1d5db',
        padding: '9px 11px',
        boxShadow: '0 1px 2px rgba(0,0,0,.06),0 1px 3px rgba(0,0,0,.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#1a3353' }}>Ageing Bands Configuration</span>
          <button onClick={handleAddBand} style={{
            fontSize: 9, fontWeight: 700, padding: '3px 8px',
            background: '#2563eb', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer',
          }}>
            + Add Band
          </button>
        </div>

        {/* Band rows */}
        {ageingBands.map(band => (
          <div key={band.id} style={{
            display: 'grid', gridTemplateColumns: '1fr 65px 65px 60px auto',
            gap: 4, alignItems: 'center', padding: '3px 0', borderBottom: '1px solid #f5f5f5',
          }}>
            {editingBandId === band.id ? (
              <>
                <input
                  value={editingBand.name || ''}
                  onChange={e => setEditingBand(prev => ({ ...prev, name: e.target.value }))}
                  style={{ fontSize: 10, border: '1px solid #2563eb', borderRadius: 3, padding: '2px 4px', outline: 'none', fontFamily: "'DM Sans', sans-serif", width: '100%' }}
                />
                <input
                  type="number"
                  value={editingBand.minDays ?? ''}
                  onChange={e => setEditingBand(prev => ({ ...prev, minDays: Number(e.target.value) }))}
                  style={{ fontSize: 10, border: '1px solid #d1d5db', borderRadius: 3, padding: '2px 4px', outline: 'none', fontFamily: "'DM Sans', sans-serif", width: '100%' }}
                />
                <input
                  type="number"
                  value={editingBand.maxDays ?? ''}
                  onChange={e => setEditingBand(prev => ({ ...prev, maxDays: Number(e.target.value) }))}
                  style={{ fontSize: 10, border: '1px solid #d1d5db', borderRadius: 3, padding: '2px 4px', outline: 'none', fontFamily: "'DM Sans', sans-serif", width: '100%' }}
                />
                <input
                  type="color"
                  value={editingBand.colorHex || '#cccccc'}
                  onChange={e => setEditingBand(prev => ({ ...prev, colorHex: e.target.value }))}
                  style={{ width: 22, height: 14, border: '1px solid #d1d5db', borderRadius: 2, cursor: 'pointer', background: 'none', padding: 0 }}
                />
                <div style={{ display: 'flex', gap: 3 }}>
                  <button onClick={() => handleUpdateBand(band.id)} style={{
                    fontSize: 8, padding: '1px 4px', background: '#f0fdf4', color: '#059669',
                    border: 'none', borderRadius: 2, cursor: 'pointer',
                  }}>✓</button>
                  <button onClick={() => { setEditingBandId(null); setEditingBand({}); }} style={{
                    fontSize: 8, padding: '1px 4px', background: '#f3f4f6', color: '#4b5563',
                    border: 'none', borderRadius: 2, cursor: 'pointer',
                  }}>✕</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: band.colorHex, flexShrink: 0 }} />
                  <span style={{ fontSize: 10, fontWeight: 600, color: '#1a3353' }}>{band.name}</span>
                </div>
                <span style={{ fontSize: 9, color: '#4b5563' }}>{band.minDays}d</span>
                <span style={{ fontSize: 9, color: '#4b5563' }}>{band.maxDays}d</span>
                <div style={{
                  width: 22, height: 14, borderRadius: 2,
                  background: band.colorHex, border: '1px solid #d1d5db',
                }} />
                <div style={{ display: 'flex', gap: 3 }}>
                  <button
                    onClick={() => {
                      setEditingBandId(band.id);
                      setEditingBand({ name: band.name, minDays: band.minDays, maxDays: band.maxDays, colorHex: band.colorHex });
                    }}
                    style={{
                      fontSize: 8, padding: '1px 4px', background: '#f3f4f6', color: '#4b5563',
                      border: 'none', borderRadius: 2, cursor: 'pointer',
                    }}
                  >✎</button>
                  <button
                    onClick={() => handleDeleteBand(band.id)}
                    style={{
                      fontSize: 8, padding: '1px 4px', background: '#fef2f2', color: '#dc2626',
                      border: 'none', borderRadius: 2, cursor: 'pointer',
                    }}
                  >✕</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Masters;
